import React from "react";
import Card from "components/card";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Pagination from "components/Pagination/Pagination";
import { MdAnalytics } from "react-icons/md";

const columnHelper = createColumnHelper();

export default function AnalyticsTable({
  tableData,
  pagination,
  onPageChange,
}) {
  const [sorting, setSorting] = React.useState([
    {
      id: "date",
      desc: true,
    },
  ]);

  const parseDate = (dateString) => {
    const year = dateString.slice(0, 4);
    const month = dateString.slice(4, 6);
    const day = dateString.slice(6, 8);
    return new Date(`${year}-${month}-${day}`);
  };

  const sortedData = React.useMemo(() => {
    return [...tableData].sort((a, b) => {
      const dateA = parseDate(a.date);
      const dateB = parseDate(b.date);
      return dateB - dateA;
    });
  }, [tableData]);

  const columns = [
    columnHelper.accessor("date", {
      id: "date",
      header: () => (
        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
          DATE
        </p>
      ),
      cell: (info) => {
        const dateString = info.getValue();
        const year = dateString.slice(0, 4);
        const month = dateString.slice(6, 4); // note: careful, originally was slice(4,6)
        const day = dateString.slice(6, 8);
        const formattedDate = `${day}-${month}-${year}`;
        return (
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {formattedDate}
          </p>
        );
      },
    }),

    columnHelper.accessor("city", {
      id: "city",
      header: () => (
        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
          CITY
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("activeUsers", {
      id: "activeUsers",
      header: () => (
        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
          ACTIVE USERS
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("pageViews", {
      id: "pageViews",
      header: () => (
        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
          PAGE VIEWS
        </p>
      ),
      cell: (info) => (
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          {info.getValue() || "0"}
        </p>
      ),
    }),
  ];

  const table = useReactTable({
    data: sortedData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Card extra="!p-[20px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <MdAnalytics className="text-xl text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Analytics
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Visitor analytics
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    onClick={header.column.getToggleSortingHandler()}
                    className="cursor-pointer border-b border-gray-200 px-4 py-3 text-left text-xs font-medium text-gray-700 dark:border-gray-600 dark:text-gray-300"
                  >
                    <div className="flex items-center gap-2">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getIsSorted() && (
                        <span className="text-primary">
                          {header.column.getIsSorted() === "desc" ? "▼" : "▲"}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="whitespace-nowrap px-4 py-3 text-gray-900 dark:text-white"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6">
        <Pagination pagination={pagination} onPageChange={onPageChange} />
      </div>
    </Card>
  );
}
